import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase/config';
import { API_ENDPOINTS, apiClient } from '../config/api';
import stateCityData from '../data/state_city.json';
import { formatUSPhoneInput, getUSPhoneError, toE164US } from '../utils/phone';
import { TX_BROKER_LICENSE_TYPE, affiliationPersonName } from '../utils/license';
import './RegisterPage.css';
import './BrokerageRegistrationPage.css';

// ── Password rules ────────────────────────────────────────────────────────────
const passwordRules = [
  { id: 'length',  label: 'At least 8 characters',  test: (p) => p.length >= 8 },
  { id: 'upper',   label: 'One uppercase letter',    test: (p) => /[A-Z]/.test(p) },
  { id: 'lower',   label: 'One lowercase letter',    test: (p) => /[a-z]/.test(p) },
  { id: 'number',  label: 'One number',              test: (p) => /[0-9]/.test(p) },
  { id: 'special', label: 'One special character',   test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p) },
];
const isPasswordValid = (pwd) => passwordRules.every(r => r.test(pwd));

// ── Phone helpers (mirrors mobile validatePhone + toE164) ─────────────────────
const validatePhone = (raw) => getUSPhoneError(raw);
const toE164 = (raw) => toE164US(raw) || raw;

const toDateInputValue = (raw) => {
  if (!raw) return '';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  } catch { return ''; }
};

const parseNameParts = (fullName) => {
  if (!fullName) return ['', ''];
  const parts = fullName.trim().split(/\s+/);
  return [parts[0] || '', parts.slice(1).join(' ') || ''];
};

const BrokerageRegistrationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const inviteData = location.state?.inviteData || {};

  const [inviteFirstName, inviteLastName] = parseNameParts(inviteData.name || '');

  // ── Section 1: Licence ────────────────────────────────────────────────────
  const [licenceState, setLicenceState] = useState(inviteData.state || '');
  const [licenceId, setLicenceId]       = useState(inviteData.brokerageLicenseId || inviteData.brokerageLicenceId || '');
  // 'idle' | 'validating' | 'valid' | 'invalid' — mirrors AgentVerificationPage's auto-verify state machine
  const [validationState,   setValidationState]   = useState('idle');
  const [validationMessage, setValidationMessage] = useState('');
  const [firmDetails, setFirmDetails]   = useState(null);
  const debounceRef = useRef(null);

  // Brokerage mailing address — pre-filled from the licence registry but
  // user-editable (mirrors mobile BrokerageRegistrationScreen: street/city/
  // state/zip are required, correctable fields that persist to user_profiles).
  const [brgAddress, setBrgAddress] = useState('');
  const [brgCity,    setBrgCity]    = useState('');
  const [brgState,   setBrgState]   = useState('');
  const [brgZip,     setBrgZip]     = useState('');

  // ── Section 2: DB email ───────────────────────────────────────────────────
  const [dbEmail, setDbEmail] = useState('');
  const [isCheckingDb, setIsCheckingDb] = useState(false);

  // ── Section 3: Contact ────────────────────────────────────────────────────
  const [firstName,       setFirstName]       = useState(inviteFirstName);
  const [lastName,        setLastName]        = useState(inviteLastName);
  const [email,           setEmail]           = useState(inviteData.email || '');
  const [phone,           setPhone]           = useState('');
  const [password,        setPassword]        = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword,    setShowPassword]    = useState(false);
  const [showConfirmPwd,  setShowConfirmPwd]  = useState(false);

  // ── Inline field errors ───────────────────────────────────────────────────
  const [fieldErrors, setFieldErrors] = useState({});
  const clearFieldErr = (key) => setFieldErrors(prev => { const n = { ...prev }; delete n[key]; return n; });

  // ── Form state ────────────────────────────────────────────────────────────
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [legalModal,    setLegalModal]    = useState(null);
  const [isSubmitting,  setIsSubmitting]  = useState(false);
  const [status,        setStatus]        = useState({ type: '', message: '' });

  const isEmailFromInvite = !!inviteData.email;

  // ── Licence verification — auto-triggered (mirrors AgentVerificationPage's
  // debounced verifyLicense, no manual button) ───────────────────────────────
  const verifyLicence = useCallback(async (license, state) => {
    if (!state || license.trim().length < 3) return;
    setValidationState('validating');
    setValidationMessage('');
    setFirmDetails(null);
    try {
      const response = await apiClient.post(API_ENDPOINTS.verifyLicense, {
        state,
        licenseNumber: license.trim(),
        role: 'BROKERAGE',
      });

      const d = response.data?.data || response.data;

      // Mirror mobile BrokerageVerificationScreen else-branch:
      // all non-'valid' statuses (not_found, expired, role_mismatch,
      // unsupported_state, role_required, invalid_role) are shown via
      // response.message — the API already provides the right user-facing
      // text for each case.
      if (d?.status !== 'valid') {
        setValidationState('invalid');
        setValidationMessage(
          d?.message || 'We could not verify this brokerage licence. Check the state and licence ID.'
        );
        return;
      }

      // Mirror mobile: DB is the first affiliation with licenseType 'Officer' or 'Broker'
      // (TX registry: 'Broker Individual' — the company's designated broker)
      const db = Array.isArray(d.affiliations)
        ? d.affiliations.find(a => a.licenseType === 'Officer' || a.licenseType === 'Broker' || a.licenseType === TX_BROKER_LICENSE_TYPE)
        : null;
      const dbName = affiliationPersonName(db);

      setFirmDetails({
        legalName:        d.fullName      || '',   // mobile: response.fullName
        dbName,                                    // mobile: [db.firstName, db.lastName].join(' ')
        dbFirstName:      db?.firstName     || '', // mobile: db.firstName
        dbLastName:       db?.lastName      || '', // mobile: db.lastName
        dbLicenceNumber:  db?.licenseNumber || '', // mobile: db.licenseNumber
        licenceStatus:    d.licenseStatus  || '',  // mobile: response.licenseStatus
        expiryDate:       toDateInputValue(d.expirationDate || ''), // mobile: response.expirationDate
        brokerageAddress: d.addressLine1   || '',  // mobile: response.addressLine1
        // City/State pulled alongside the zip from the main verify-license record
        // (same source object as brokerageAddress) — mirrors AgentVerificationPage/
        // BrokerVerificationPage's verificationResponse?.city/.state/.zipCode.
        brokerageCity:     d.city          || '',
        brokerageState:    d.state         || '',
        brokerageZip:      d.zipCode       || '',
        brokeragePhone:   '',                      // not stored in CA DRE (mirrors mobile)
        verificationId:   d.id             || '',  // mobile: verificationResponse?.id
      });
      // Seed the editable mailing-address fields from the same registry
      // record — user can correct/complete them before submitting (mirrors
      // mobile BrokerageRegistrationScreen's addressLine/city/addressState/postalCode).
      setBrgAddress(d.addressLine1 || '');
      setBrgCity(d.city || '');
      setBrgState((d.state || state || '').replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 2));
      setBrgZip((d.zipCode || '').replace(/\D/g, '').slice(0, 9));
      // Auto-populate First/Last name from the BROKERAGE (Corporation) record's
      // own firstName/lastName on the licence-registry response — NOT the
      // Designated Broker affiliation (mirrors mobile BrokerageRegistrationScreen's
      // brokerageFirstName/brokerageLastName). CA DRE Corporation rows store the
      // firm name in `lastName` with `firstName` null (e.g. lastName: "SanSan
      // Investments Inc"), so Last name will typically hold the full legal firm
      // name. Only fills empty fields, so it never overwrites an invite-prefilled
      // or user-typed name.
      setFirstName(prev => prev || (d.firstName || '').trim());
      setLastName(prev => prev || (d.lastName || '').trim());
      setValidationState('valid');
      setValidationMessage('Brokerage licence verified successfully!');
    } catch (err) {
      setValidationState('invalid');
      setValidationMessage(err.response?.data?.message || 'Unable to verify licence. Please try again.');
    }
  }, []);

  // ── Field change handlers — auto-verify after a 1s debounce, matching
  // AgentVerificationPage's handleStateChange/handleLicenseChange ───────────
  const handleStateChange = (e) => {
    setLicenceState(e.target.value);
    setFirmDetails(null);
    setBrgAddress(''); setBrgCity(''); setBrgState(''); setBrgZip('');
    setValidationState('idle');
    setValidationMessage('');
    setDbEmail('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
  };

  const handleLicenceIdChange = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    setLicenceId(value);
    setFirmDetails(null);
    setBrgAddress(''); setBrgCity(''); setBrgState(''); setBrgZip('');
    setValidationState('idle');
    setValidationMessage('');
    setDbEmail('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (licenceState && value.trim().length >= 3) {
      debounceRef.current = setTimeout(() => verifyLicence(value, licenceState), 1000);
    }
  };

  // Auto-verify on mount when the invite already pre-fills both fields —
  // preserves the old "click Verify Licence" flow for invited brokerages.
  useEffect(() => {
    if (licenceState && licenceId.trim().length >= 3) {
      verifyLicence(licenceId, licenceState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};

    if (!licenceState || !licenceId.trim()) {
      setStatus({ type: 'error', message: 'Please enter and verify your brokerage licence before submitting.' });
      return false;
    }
    if (!firmDetails) {
      setStatus({ type: 'error', message: 'Please verify your brokerage licence first.' });
      return false;
    }
    if (!dbEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dbEmail.trim())) {
      errs.dbEmail = 'Enter a valid Designated Broker email address.';
    }
    if (!firstName.trim()) errs.firstName = 'First name is required';
    if (!lastName.trim())  errs.lastName  = 'Last name is required';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errs.email = 'Enter a valid email address.';
    }
    const phoneErr = validatePhone(phone);
    if (phoneErr) errs.phone = phoneErr;

    if (!brgAddress.trim()) errs.address = 'Brokerage address is required.';
    if (!brgCity.trim()) errs.city = 'City is required.';
    if (!brgState.trim() || brgState.trim().length !== 2) errs.state = 'Enter a 2-letter state code.';
    const zipDigits = brgZip.replace(/\D/g, '');
    if (!zipDigits || (zipDigits.length !== 5 && zipDigits.length !== 9)) errs.zip = 'Enter a valid 5 or 9-digit ZIP code.';

    if (!isPasswordValid(password)) errs.password = 'Password does not meet the requirements.';
    if (password !== confirmPassword) errs.confirmPassword = 'Passwords do not match.';

    if (!agreedToTerms) {
      setFieldErrors(errs);
      setStatus({ type: 'error', message: 'You must agree to the Terms of Service and Privacy Policy to register.' });
      return false;
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setStatus({ type: 'error', message: Object.values(errs)[0] });
      return false;
    }
    return true;
  };

  // ── Designated Broker email check ─────────────────────────────────────────
  // Ask the backend whether this email is already the Designated Broker of
  // another brokerage. Public endpoint — no auth required at this stage of
  // the activation flow (mirrors mobile BrokerageVerificationScreen.handleContinue).
  const checkDesignatedBrokerAvailable = async () => {
    setIsCheckingDb(true);
    try {
      const { data } = await apiClient.post(API_ENDPOINTS.checkDesignatedBroker, {
        email: dbEmail.trim(),
      });
      if (data && data.available === false) {
        const firmName = data.brokerageName || 'another brokerage';
        setFieldErrors(prev => ({
          ...prev,
          dbEmail: `This person is already the Designated Broker of ${firmName}. Enter a different email.`,
        }));
        return false;
      }
      // available === true (or unexpected shape → fail-open to preserve
      // existing working flow if the check endpoint is down).
      return true;
    } catch (err) {
      console.warn('[BrokerageRegistration] DB-check failed, proceeding:', err?.message);
      // Fail-open: don't block activation on a check failure — preserves
      // the original working behaviour if the endpoint is unreachable.
      return true;
    } finally {
      setIsCheckingDb(false);
    }
  };

  // ── Activation ────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    if (!validate()) return;
    if (isCheckingDb) return;

    const dbAvailable = await checkDesignatedBrokerAvailable();
    if (!dbAvailable) {
      setStatus({ type: 'error', message: 'Please enter a different Designated Broker email address.' });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: 'info', message: 'Creating your account…' });

    const zipDigits = brgZip.replace(/\D/g, '');

    try {
      const userCred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const firebaseToken = await userCred.user.getIdToken();
      const authHeaders = { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };

      setStatus({ type: 'info', message: 'Completing registration…' });
      try {
        await apiClient.post(API_ENDPOINTS.register, {
          // ── Account-creation fields — required by the backend to link this
          // request to the Firebase user just created; kept as-is so account
          // creation keeps working regardless of the fields added below.
          firebaseUid: userCred.user.uid,
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: toE164(phone),
          role: 'BROKERAGE',
          brokerageName: firmDetails?.legalName || '',
          licenseState: licenceState,
          // Mailing-address state (user-editable) — mirrors RegisterPage.js's
          // and mobile's contract where `state` is the address state, distinct
          // from `licenseState`.
          state: brgState.trim().toUpperCase(),
          brokerOfRecordName: firmDetails?.dbName || '',
          brokerageAddress: brgAddress.trim(),
          // ── Brokerage-activation fields (mirrors mobile BrokerageRegistrationScreen's
          // register payload) ──────────────────────────────────────────────────────
          invitationKey: inviteData.key || '',
          brokerLicenseNumber: licenceId.trim(),
          legalBrokerageName: firmDetails?.legalName || inviteData.companyName || '',
          brokerageFirstName: firstName.trim(),
          brokerageLastName: lastName.trim(),
          designatedBrokerName: firmDetails?.dbName || '',
          designatedBrokerFirstName: firmDetails?.dbFirstName || '',
          designatedBrokerLastName: firmDetails?.dbLastName || '',
          designatedBrokerLicenseNumber: firmDetails?.dbLicenceNumber || '',
          licenseStatus: firmDetails?.licenceStatus || '',
          licenseExpirationDate: firmDetails?.expiryDate || '',
          brokerageCity: brgCity.trim(),
          brokerageZip: zipDigits,
          brokeragePhone: firmDetails?.brokeragePhone || '',
          designatedBrokerEmail: dbEmail.trim(),
          verificationId: firmDetails?.verificationId || '',
          inviteEmail: inviteData.email || '',
          inviteName: inviteData.name || '',
          // ── Split mailing-address fields for user_profiles (mirrors mobile
          // BrokerageRegistrationScreen: street/city/state/postalCode land on
          // UserProfile.addressLine1/city/state/zipCode) ──────────────────────
          street: brgAddress.trim() || undefined,
          city: brgCity.trim() || undefined,
          postalCode: zipDigits || undefined,
        }, { headers: authHeaders });
      } catch (apiErr) {
        const isExists =
          apiErr.response?.status === 409 ||
          apiErr.response?.data?.message?.toLowerCase().includes('already exists');
        if (!isExists) {
          try { await userCred.user.delete(); } catch { /* cleanup */ }
          throw apiErr;
        }
      }

      if (inviteData.key) {
        try {
          await apiClient.post(API_ENDPOINTS.acceptUserInvite(inviteData.key), {}, { headers: authHeaders });
        } catch { /* non-critical */ }
      }

      // Fire-and-forget Designated Broker activation invite (mirrors mobile BrokerageRegistrationScreen:
      // createInvitation writes the invitation record, then a separate email/invite/send call actually
      // dispatches the email — createInvitation alone does not send it).
      if (dbEmail.trim()) {
        apiClient.post(API_ENDPOINTS.createInvitation, {
          role: 'BROKER',
          email: dbEmail.trim(),
          isShareable: false,
          companyName: firmDetails?.legalName || '',
          brokerageLicenseId: licenceId.trim(),
          isDesignatedBrokerInvite: true,
        }, { headers: authHeaders })
          .then((inv) => {
            const invitationKey = inv.data?.invitationKey || inv.data?.key || inv.data?.data?.invitationKey;
            console.log('[BrokerageRegistration] DB broker invite created:', invitationKey);
            if (!invitationKey) return;
            const inviterName = `${firstName.trim()} ${lastName.trim()}`.trim() || firmDetails?.legalName || 'A Rialty user';
            return apiClient.post(API_ENDPOINTS.sendInviteEmail, {
              invitationKey,
              email: dbEmail.trim(),
              inviteeName: 'Designated Broker',
              role: 'BROKER',
              inviterName,
              inviterEmail: email.trim(),
              welcomeNote: firmDetails?.legalName
                ? `You've been invited to register as the Designated Broker of ${firmDetails.legalName} on Rialty.`
                : 'You\'ve been invited to register as a Designated Broker on Rialty.',
            }, { headers: authHeaders })
              .then(() => console.log('[BrokerageRegistration] DB broker invite email sent'));
          })
          .catch((err) => console.warn('[BrokerageRegistration] DB broker invite failed (non-fatal):', err?.message));
      }

      setStatus({ type: 'success', message: 'Registration complete! Redirecting to login…' });
      setTimeout(() => navigate('/login', {
        replace: true,
        state: { registrationSuccess: true, prefillEmail: email.trim() },
      }), 1500);
    } catch (err) {
      const code = err.code || '';
      if (code === 'auth/email-already-in-use') {
        setStatus({ type: 'error', message: 'This email is already registered. Please login instead.' });
      } else if (code === 'auth/weak-password') {
        setStatus({ type: 'error', message: 'Password is too weak. Please use a stronger password.' });
      } else if (code === 'auth/network-request-failed') {
        setStatus({ type: 'error', message: 'Network error. Please check your connection.' });
      } else {
        setStatus({ type: 'error', message: err.response?.data?.message || err.message || 'Registration failed. Please try again.' });
      }
      setIsSubmitting(false);
    }
  };

  // Mirror mobile: LICENSED + ACTIVE + VERIFIED all render as "active" (green)
  const isStatusActive = (s) => s && (
    s.toUpperCase() === 'ACTIVE' ||
    s.toUpperCase() === 'VERIFIED' ||
    s.toUpperCase() === 'LICENSED'
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="register-page">
      <div className="registration-container">

        {/* Header */}
        <div className="header-section">
          <h1 className="title">Brokerage Registration</h1>
          <p className="subtitle">Verify your licence and complete your account setup</p>
        </div>

        <form onSubmit={handleSubmit}>

          {/* ── SECTION 1: Brokerage Licence (layout mirrors AgentVerificationPage — auto-verify, no button) ── */}
          <div className="brg-section-label">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>
            </svg>
            Brokerage Licence
          </div>

          <div className="brg-info-notice">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>Enter your state and brokerage licence ID — we'll pull your firm details automatically from the licence registry.</span>
          </div>

          <div className="form-group">
            <select
              value={licenceState}
              onChange={handleStateChange}
              className={`form-select${!licenceState ? ' placeholder' : ''}`}
            >
              <option value="" disabled>Select state</option>
              {stateCityData.map(item => (
                <option key={item.state} value={item.stateCode}>{item.state} ({item.stateCode})</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <div className="license-input-wrapper">
              <input
                type="text"
                placeholder="Brokerage licence ID"
                value={licenceId}
                onChange={handleLicenceIdChange}
                maxLength={32}
                className={`form-input${validationState === 'invalid' ? ' input-error' : ''}${validationState === 'valid' ? ' input-success' : ''}`}
              />
              {validationState === 'validating' && (
                <span className="license-verifying">Verifying...</span>
              )}
            </div>
          </div>

          {validationState !== 'idle' && validationMessage && (
            <div className={`av-validation-row av-validation-${validationState}`}>
              <span className="av-validation-icon">
                {validationState === 'valid'      ? '✓'
                : validationState === 'validating' ? '…'
                :                                   '⚠'}
              </span>
              <span className="av-validation-text">{validationMessage}</span>
            </div>
          )}

          {/* ── SECTION 2: Firm Details (readonly — pulled from licence registry) ── */}
          {firmDetails && (
            <>
              <div className="brg-section-label" style={{ marginTop: 28 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
                </svg>
                Firm Details
                <span className="brg-pulled-chip">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Pulled from licence registry
                </span>
              </div>

              <div className="brg-success-notice">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>Licence verified. Firm details have been auto-populated from the licence registry.</span>
              </div>

              {/* Legal brokerage name — full width, readonly */}
              <div className="form-group">
                <label className="brg-field-label-above">Legal brokerage name</label>
                <div className="brg-readonly-field">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
                  </svg>
                  <span>{firmDetails.legalName || '—'}</span>
                </div>
              </div>

              {/* Designated Broker name | DB licence number — 2 columns, readonly */}
              <div className="form-row">
                <div className="brg-form-field">
                  <label className="brg-field-label-above">Designated Broker (Officer) name</label>
                  <div className="brg-readonly-field">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                    </svg>
                    <span>{firmDetails.dbName || '—'}</span>
                  </div>
                </div>
                <div className="brg-form-field">
                  <label className="brg-field-label-above">DB (Officer) licence number</label>
                  <div className="brg-readonly-field">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    <span>{firmDetails.dbLicenceNumber || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Licence status | Expiry date — 2 columns, readonly */}
              <div className="form-row">
                <div className="brg-form-field">
                  <label className="brg-field-label-above">Licence status</label>
                  <div className="brg-readonly-field">
                    <span className={`brg-status-badge ${isStatusActive(firmDetails.licenceStatus) ? 'brg-status-badge--active' : 'brg-status-badge--other'}`}>
                      {isStatusActive(firmDetails.licenceStatus) && (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                      {firmDetails.licenceStatus || '—'}
                    </span>
                  </div>
                </div>
                <div className="brg-form-field">
                  <label className="brg-field-label-above">Expiry date</label>
                  <div className="brg-readonly-field">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    <span>{firmDetails.expiryDate || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Designated Broker email — editable (required for invite) */}
              <div className="form-group" style={{ marginTop: 8 }}>
                <label className="brg-field-label-above">
                  Designated Broker email <span className="brg-required">*</span>
                </label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    className={`form-input${fieldErrors.dbEmail ? ' brg-input-error' : ''}`}
                    placeholder="DB's email address — a Broker role invite will be sent here on activation"
                    value={dbEmail}
                    onChange={e => { setDbEmail(e.target.value); clearFieldErr('dbEmail'); }}
                  />
                  <span className="input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </span>
                </div>
                {fieldErrors.dbEmail
                  ? <p className="brg-field-error">{fieldErrors.dbEmail}</p>
                  : <p className="brg-field-helper">A Broker role invite will be sent here on activation.</p>
                }
              </div>
            </>
          )}

          {/* ── SECTION 3: Contact Details (hidden until licence is verified) ── */}
          {firmDetails && (<><div className="brg-section-label" style={{ marginTop: 28 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
            Contact Details
          </div>

          <div className="brg-info-notice">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>This will be the Brokerage account login on Rialty.{isEmailFromInvite ? ' Email is pre-filled from the invite.' : ''}</span>
          </div>

          {/* First + Last — 2 columns (wireframe + mobile layout) */}
          <div className="form-row">
            <div className="brg-form-field">
              <label className="brg-field-label-above">First name <span className="brg-required">*</span></label>
              <div className="input-wrapper">
                <input
                  type="text"
                  className={`form-input${fieldErrors.firstName ? ' brg-input-error' : ''}`}
                  placeholder="First name"
                  value={firstName}
                  onChange={e => { setFirstName(e.target.value); clearFieldErr('firstName'); }}
                />
                <span className="input-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                </span>
              </div>
              {fieldErrors.firstName && <p className="brg-field-error">{fieldErrors.firstName}</p>}
            </div>
            <div className="brg-form-field">
              <label className="brg-field-label-above">Last name <span className="brg-required">*</span></label>
              <div className="input-wrapper">
                <input
                  type="text"
                  className={`form-input${fieldErrors.lastName ? ' brg-input-error' : ''}`}
                  placeholder="Last name"
                  value={lastName}
                  onChange={e => { setLastName(e.target.value); clearFieldErr('lastName'); }}
                />
                <span className="input-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                </span>
              </div>
              {fieldErrors.lastName && <p className="brg-field-error">{fieldErrors.lastName}</p>}
            </div>
          </div>

          {/* Email */}
          <div className="form-group">
            <label className="brg-field-label-above">Email address <span className="brg-required">*</span></label>
            <div className="input-wrapper">
              <input
                type="email"
                className={`form-input${isEmailFromInvite ? ' input-readonly' : ''}${fieldErrors.email ? ' brg-input-error' : ''}`}
                placeholder="Email address"
                value={email}
                readOnly={isEmailFromInvite}
                onChange={e => { if (!isEmailFromInvite) { setEmail(e.target.value); clearFieldErr('email'); } }}
              />
              <span className="input-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                </svg>
              </span>
            </div>
            {isEmailFromInvite
              ? <p className="brg-field-helper">Auto-filled from invite — this is the Brokerage account login</p>
              : fieldErrors.email && <p className="brg-field-error">{fieldErrors.email}</p>
            }
          </div>

          {/* Phone */}
          <div className="form-group">
            <label className="brg-field-label-above">Phone number <span className="brg-required">*</span></label>
            <div className="input-wrapper">
              <input
                type="tel"
                className={`form-input${fieldErrors.phone ? ' brg-input-error' : ''}`}
                placeholder="Phone number"
                value={phone}
                onChange={e => {
                  setPhone(formatUSPhoneInput(e.target.value));
                  clearFieldErr('phone');
                }}
              />
              <span className="input-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
              </span>
            </div>
            {fieldErrors.phone && <p className="brg-field-error">{fieldErrors.phone}</p>}
          </div>

          {/* Brokerage address — street, then City/State/Zip. Pre-filled from
              licence verification but editable, so the user can correct or
              complete a bad/incomplete registry address before activation
              (mirrors mobile BrokerageRegistrationScreen's addressLine/city/
              addressState/postalCode — all required, user-correctable fields
              that persist to user_profiles). */}
          <div className="form-group">
            <label className="brg-field-label-above">Brokerage address <span className="brg-required">*</span></label>
            <div className="input-wrapper">
              <input
                type="text"
                className={`form-input${fieldErrors.address ? ' brg-input-error' : ''}`}
                placeholder="Street address"
                value={brgAddress}
                onChange={e => { setBrgAddress(e.target.value); clearFieldErr('address'); }}
              />
              <span className="input-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                </svg>
              </span>
            </div>
            {fieldErrors.address
              ? <p className="brg-field-error">{fieldErrors.address}</p>
              : <p className="brg-field-helper">Pre-filled from licence verification — edit if needed</p>
            }
          </div>

          <div className="brg-form-row-3">
            <div className="brg-form-field">
              <label className="brg-field-label-above">City <span className="brg-required">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors.city ? ' brg-input-error' : ''}`}
                placeholder="City"
                value={brgCity}
                onChange={e => { setBrgCity(e.target.value); clearFieldErr('city'); }}
              />
              {fieldErrors.city && <p className="brg-field-error">{fieldErrors.city}</p>}
            </div>
            <div className="brg-form-field">
              <label className="brg-field-label-above">State <span className="brg-required">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors.state ? ' brg-input-error' : ''}`}
                placeholder="CA"
                value={brgState}
                maxLength={2}
                onChange={e => {
                  const upper = e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 2);
                  setBrgState(upper);
                  clearFieldErr('state');
                }}
              />
              {fieldErrors.state && <p className="brg-field-error">{fieldErrors.state}</p>}
            </div>
            <div className="brg-form-field">
              <label className="brg-field-label-above">Zip code <span className="brg-required">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors.zip ? ' brg-input-error' : ''}`}
                placeholder="ZIP"
                value={brgZip}
                maxLength={10}
                onChange={e => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                  setBrgZip(digits);
                  clearFieldErr('zip');
                }}
              />
              {fieldErrors.zip && <p className="brg-field-error">{fieldErrors.zip}</p>}
            </div>
          </div>

          {/* Password | Confirm — 2 columns (wireframe + mobile) */}
          <div className="form-row" style={{ alignItems: 'flex-start' }}>
            <div className="brg-form-field">
              <label className="brg-field-label-above">Password <span className="brg-required">*</span></label>
              <div className="input-wrapper password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`form-input${fieldErrors.password ? ' brg-input-error' : ''}`}
                  placeholder="Create a password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); clearFieldErr('password'); }}
                />
                <button type="button" className="password-toggle-btn" onClick={() => setShowPassword(v => !v)} tabIndex="-1">
                  {showPassword
                    ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  }
                </button>
              </div>
              {fieldErrors.password && <p className="brg-field-error">{fieldErrors.password}</p>}
            </div>
            <div className="brg-form-field">
              <label className="brg-field-label-above">Confirm password <span className="brg-required">*</span></label>
              <div className="input-wrapper password-wrapper">
                <input
                  type={showConfirmPwd ? 'text' : 'password'}
                  className={`form-input${fieldErrors.confirmPassword ? ' brg-input-error' : ''}`}
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={e => { setConfirmPassword(e.target.value); clearFieldErr('confirmPassword'); }}
                />
                <button type="button" className="password-toggle-btn" onClick={() => setShowConfirmPwd(v => !v)} tabIndex="-1">
                  {showConfirmPwd
                    ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  }
                </button>
              </div>
              {fieldErrors.confirmPassword && <p className="brg-field-error">{fieldErrors.confirmPassword}</p>}
            </div>
          </div>

          {/* Password requirements */}
          {password.length > 0 && (
            <div className="reg-requirements" style={{ marginTop: 8 }}>
              {passwordRules.map(rule => (
                <div key={rule.id} className={`reg-req-item ${rule.test(password) ? 'met' : ''}`}>
                  <span className="reg-req-icon">
                    {rule.test(password)
                      ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                      : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/></svg>
                    }
                  </span>
                  <span>{rule.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Broker invite green notice — shows DB email (mirrors mobile) */}
          <div className="brg-success-notice" style={{ marginTop: 20, marginBottom: 20 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
            <span>
              On activation, a <strong>Broker role invite</strong> will be automatically sent to the Designated Broker's email
              {dbEmail.trim() ? ` (${dbEmail.trim()})` : ''} with all brokerage permissions. DB will receive a separate invite to join Rialty as a Broker linked to this brokerage.
            </span>
          </div>

          {/* Terms */}
          <div className="reg-terms-row">
            <input
              type="checkbox"
              id="agreeTerms"
              className="reg-terms-checkbox"
              checked={agreedToTerms}
              onChange={e => setAgreedToTerms(e.target.checked)}
            />
            <label htmlFor="agreeTerms" className="reg-terms-label">
              Yes, I understand and agree to the{' '}
              <button type="button" className="reg-terms-link" onClick={() => setLegalModal('terms')}>Rialty Terms of Service</button>
              {' '}and{' '}
              <button type="button" className="reg-terms-link" onClick={() => setLegalModal('privacy')}>Privacy Policy</button>.
            </label>
          </div>

          {/* Status */}
          {status.message && (
            <div className={`status-message ${status.type}`}>{status.message}</div>
          )}

          {/* Action buttons — wireframe: Cancel | Activate */}
          <div className="brg-action-row">
            <button
              type="button"
              className="brg-cancel-btn"
              onClick={() => navigate(-1)}
              disabled={isSubmitting || isCheckingDb}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="register-btn brg-activate-btn"
              disabled={isSubmitting || isCheckingDb || !agreedToTerms}
            >
              {isSubmitting ? (
                <>
                  <span className="brg-spinner-dark brg-spinner-white" />
                  Activating brokerage…
                </>
              ) : isCheckingDb ? (
                <>
                  <span className="brg-spinner-dark brg-spinner-white" />
                  Checking Designated Broker…
                </>
              ) : (
                <>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Activate brokerage
                </>
              )}
            </button>
          </div>

          <div className="auth-footer">
            <p>Already have an account? <Link to="/login" className="link">Login</Link></p>
          </div>
          </>)}
        </form>
      </div>

      {/* ── Legal Modal ───────────────────────────────────────────────────────── */}
      {legalModal && (
        <div className="reg-legal-overlay" onClick={() => setLegalModal(null)}>
          <div className="reg-legal-modal" onClick={e => e.stopPropagation()}>
            <div className="reg-legal-header">
              <h2 className="reg-legal-title">
                {legalModal === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
              </h2>
              <button type="button" className="reg-legal-close" onClick={() => setLegalModal(null)} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="reg-legal-body">
              {legalModal === 'terms' ? (
                <>
                  <p className="reg-legal-date">Last Updated: November 25, 2025</p>
                  <h3>1. Acceptance of Terms</h3>
                  <p>By accessing and using Rialty.ai ("the Platform"), you accept and agree to be bound by these Terms of Service. If you do not agree, please do not use the Platform.</p>
                  <h3>2. Description of Service</h3>
                  <p>Rialty.ai is an intelligent transaction management platform designed to streamline real estate workflows for professionals, buyers, and sellers.</p>
                  <h3>3. User Accounts and Registration</h3>
                  <p><strong>3.1 Account Creation:</strong> You agree to provide accurate, current, and complete information during registration.</p>
                  <p><strong>3.2 Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials.</p>
                  <h3>4. User Conduct</h3>
                  <p>You agree not to use the Platform for unlawful purposes, upload malicious code, interfere with the Platform, or impersonate others.</p>
                  <h3>5. Intellectual Property</h3>
                  <p>The Platform and its content are owned by Rialty.ai and protected by intellectual property laws.</p>
                  <h3>6. Governing Law</h3>
                  <p>These Terms shall be governed by applicable laws of the United States. Contact us at <strong>legal@rialty.ai</strong>.</p>
                </>
              ) : (
                <>
                  <p className="reg-legal-date">Last Updated: November 25, 2025</p>
                  <h3>1. Introduction</h3>
                  <p>Rialty.ai is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and protect your information.</p>
                  <h3>2. Information We Collect</h3>
                  <p>We collect name, email, phone, professional information, and usage data to provide our services.</p>
                  <h3>3. How We Use Your Information</h3>
                  <p>To provide and improve services, process transactions, communicate with you, and comply with legal obligations.</p>
                  <h3>4. Data Security</h3>
                  <p>We implement industry-standard security measures including encryption and access controls.</p>
                  <h3>5. Contact Us</h3>
                  <p>Questions? Contact us at <strong>privacy@rialty.ai</strong>.</p>
                </>
              )}
            </div>
            <div className="reg-legal-footer">
              <button type="button" className="reg-legal-agree-btn" onClick={() => { setAgreedToTerms(true); setLegalModal(null); }}>
                I have read and agree
              </button>
              <button type="button" className="reg-legal-dismiss-btn" onClick={() => setLegalModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrokerageRegistrationPage;
